import React, { useEffect, useRef, useCallback, useState } from "react";
import { Spinner } from "react-bootstrap";

// Components
import Navbar from "./components/Navbar";
import ErrorPage from "./components/ErrorPage";
import CodePanel from "./components/layout/CodePanel";
import PreviewPanel from "./components/layout/PreviewPanel";
import ResizablePane from "./components/ResizablePane";

// Hooks
import {
  useFileManager,
  usePublishing,
  useEditorState,
  useIframeEvents,
  useImageCropping,
  useEditorCallbacks,
} from "./hooks";

// Context
import { EditorProvider } from "./contexts";
import { isAIEnabled } from "./featureFlag";
import { scriptsDiffer } from "./services/editor";

// Assets
import checkSVG from "./assets/images/check.svg";
import logo from "./assets/svgs/logo.svg";

import "react-confirm-alert/src/react-confirm-alert.css";
import "./App.css";

function App() {
  // URL params
  const searchParams = new URLSearchParams(window.location.search);
  const domain = searchParams.get("domain");
  const initialFile = searchParams.get("file");
  const accessToken = localStorage.getItem("token");
  const isTrial = searchParams.get("trial") === "true";
  const isPasswordPage = searchParams.get("customize-auth") === "true";
  // Arrived from the AI creator's publish step: start collapsed (live preview,
  // Files hidden) since the user just built this site here, not by hand.
  const fromCreator = searchParams.get("from") === "ai-creator";
  const isMobileViewport = window.innerWidth <= 991;

  // Refs
  const iframeRef = useRef(null);
  const editorRef = useRef(null);

  // Hooks
  const fileManager = useFileManager(domain, initialFile, isPasswordPage);
  const editorState = useEditorState({ startCollapsed: fromCreator });
  const publishing = usePublishing(domain, editorState.clearChanged);
  const imageCropping = useImageCropping();
  const [aiPaywallVisible, setAiPaywallVisible] = useState(false);
  const [aiPaywallProfile, setAiPaywallProfile] = useState(null);

  // Check if AI is enabled based on user's plan (from JWT token); never on
  // the auth-customizer page, since AI editing doesn't apply there.
  const aiEnabled = isAIEnabled() && !isPasswordPage;

  // Destructure for convenience
  const {
    currentFile,
    activeHtmlPage,
    iframeSrc,
    saveFileContent,
    createNewFile,
    switchFile,
    refreshCode,
    getPreviewHtml,
    showPreview,
    previewHtmlRef,
  } = fileManager;
  const { markChanged, selectedLink, showElementPanel, hidePanel } =
    editorState;

  const handleFilesClick = () => {
    const isPanelOpen = !editorState.webViewMode;
    if (isPanelOpen && editorState.panelMode === "files") {
      editorState.setPanelMode("code");
      editorState.setIsFileExplorerVisible(false);
      return;
    }
    editorState.setWebViewMode(false);
    editorState.setPanelMode("files");
    editorState.setSidePanelView("code");
    editorState.setIsFileExplorerVisible(true);
    editorState.setShowCodeEditor(true);
  };

  const handleCodeClick = () => {
    const isPanelOpen = !editorState.webViewMode;
    if (isPanelOpen && editorState.panelMode === "code") {
      editorState.setWebViewMode(true);
      return;
    }
    editorState.setWebViewMode(false);
    editorState.setPanelMode("code");
    editorState.setSidePanelView("code");
    editorState.setIsFileExplorerVisible(false);
    editorState.setShowCodeEditor(true);
  };

  const handleAIClick = () => {
    if (!aiEnabled) return;
    const isPanelOpen = !editorState.webViewMode;
    if (isPanelOpen && editorState.panelMode === "ai") {
      editorState.setWebViewMode(true);
      return;
    }
    editorState.setWebViewMode(false);
    editorState.setPanelMode("ai");
    editorState.setSidePanelView("ai");
    editorState.setIsFileExplorerVisible(false);
    editorState.setShowCodeEditor(false);
  };

  const handleAIPaywallRequired = useCallback((profile) => {
    setAiPaywallProfile(profile || null);
    setAiPaywallVisible(true);
  }, []);

  const closeAnyUpgradeModal = useCallback(() => {
    setAiPaywallVisible(false);
    publishing.closeUpgradeModal();
  }, [publishing]);

  // Editor callbacks hook
  const callbacks = useEditorCallbacks({
    iframeRef,
    currentFile,
    selectedLink,
    saveFileContent,
    markChanged,
    createNewFile,
    switchFile,
  });

  // Iframe events hook
  const iframeEvents = useIframeEvents(iframeRef, {
    onElementClick: (e) => {
      markChanged();
      showElementPanel(e.target, false);
    },
    onLinkClick: (link) => {
      markChanged();
      showElementPanel(link, true);
    },
    onImageClick: (image) => {
      markChanged();
      imageCropping.handleImageUpload(image);
    },
    onScroll: hidePanel,
    onKeyUp: callbacks.handleChangesDebounced,
    onEscape: hidePanel,
    markChanged,
  });

  // AI edits patch the preview in place, so the page stays where the user left it.
  // Only a real load runs scripts, so a script change reloads with the scroll kept.
  const { applyPreviewHtml, keepScrollAcrossReload } = iframeEvents;
  const handleAIFilesChanged = useCallback(
    async (changedFiles) => {
      markChanged();
      if (changedFiles.includes(currentFile)) await refreshCode();
      if (!changedFiles.includes(activeHtmlPage)) return;

      const html = await getPreviewHtml();
      if (scriptsDiffer(previewHtmlRef.current, html)) {
        keepScrollAcrossReload();
        showPreview(html);
      } else {
        applyPreviewHtml(html);
        previewHtmlRef.current = html;
      }
    },
    [
      markChanged,
      currentFile,
      refreshCode,
      activeHtmlPage,
      getPreviewHtml,
      previewHtmlRef,
      keepScrollAcrossReload,
      showPreview,
      applyPreviewHtml,
    ],
  );

  // Initialize: Fetch files when database is ready
  const { dbReady, fetchAllFiles } = fileManager;
  useEffect(() => {
    if (dbReady) {
      fetchAllFiles();
    }
  }, [dbReady, fetchAllFiles]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!aiEnabled && editorState.panelMode === "ai") {
      editorState.setPanelMode("code");
      editorState.setSidePanelView("code");
      editorState.setIsFileExplorerVisible(false);
      editorState.setShowCodeEditor(true);
    }
  }, [aiEnabled, editorState]);

  // Publishing reads the saved copy, so a debounced edit still in flight has to
  // land first - otherwise the last thing the user typed is not in what ships.
  // A failure to flush must not swallow the publish itself: the button would go
  // dead with no spinner and no message.
  const handlePublish = useCallback(async () => {
    try {
      await callbacks.flushChanges();
    } catch (err) {
      console.error('Could not save pending edits before publishing:', err);
    }
    return publishing.publish();
    // Depends on the two functions, not on their containers: both hooks return a
    // fresh object every render, so listing those would rebuild this on every
    // keystroke. The rule cannot see through the member access.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbacks.flushChanges, publishing.publish]);

  // Iframe load handler
  const handleIframeLoad = useCallback(() => {
    iframeEvents.setupEventListeners();
  }, [iframeEvents]);

  // Utility
  const isSmallScreen = window.innerWidth < 769;

  // Error state
  if (fileManager.error) return <ErrorPage />;

  return (
    <EditorProvider
      domain={domain}
      accessToken={accessToken}
      dbRef={fileManager.dbRef}
    >
      <div
        className={`app-layout ${editorState.hasChanged ? "has-mobile-actions" : ""}`}
      >
        <Navbar
          domain={domain}
          hasChanged={editorState.hasChanged}
          handleDiscard={() => publishing.discard(fileManager.fetchAllFiles)}
          handlePublish={handlePublish}
          isTrial={isTrial}
          isPublishing={publishing.isPublishing}
          checkSVG={checkSVG}
          filename={activeHtmlPage}
          aiEnabled={aiEnabled}
          isDiscarding={publishing.isDiscarding}
          isSaving={fileManager.isSaving}
          pickerMessage={
            editorState.isPickerActive
              ? "Click an element to select it (ESC to cancel)"
              : null
          }
          logoSrc={logo}
          leftControls={
            !isPasswordPage ? (
              <div className="nav-left-controls">
                {!fromCreator && (
                <button
                  type="button"
                  className={`nav-pill-btn ${!editorState.webViewMode && editorState.panelMode === "files" ? "active" : ""}`}
                  onClick={handleFilesClick}
                >
                  <span className="nav-pill-icon" aria-hidden>
                    <svg
                      width="13"
                      height="11"
                      viewBox="0 0 13 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.601562 5.84961V5.41211C0.601562 4.68724 1.18919 4.09961 1.91406 4.09961H10.6641C11.3889 4.09961 11.9766 4.68724 11.9766 5.41211V5.84961M6.90778 2.09333L5.67034 0.855891C5.50625 0.691797 5.28369 0.599609 5.05163 0.599609H1.91406C1.18919 0.599609 0.601562 1.18724 0.601562 1.91211V8.91211C0.601562 9.63698 1.18919 10.2246 1.91406 10.2246H10.6641C11.3889 10.2246 11.9766 9.63698 11.9766 8.91211V3.66211C11.9766 2.93724 11.3889 2.34961 10.6641 2.34961H7.5265C7.29444 2.34961 7.07188 2.25742 6.90778 2.09333Z"
                        stroke="black"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  Files
                </button>
                )}
                <button
                  type="button"
                  className={`nav-pill-btn ${!editorState.webViewMode && editorState.panelMode === "code" ? "active" : ""}`}
                  onClick={handleCodeClick}
                >
                  <span className="nav-pill-icon" aria-hidden>
                    <svg
                      width="14"
                      height="11"
                      viewBox="0 0 14 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.78906 2.35059L12.8516 5.41309L9.78906 8.47559M3.66406 8.47559L0.601562 5.41309L3.66406 2.35059M8.03906 0.600586L5.41406 10.2256"
                        stroke="#1E1E1E"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  Code
                </button>
                {aiEnabled && (
                  <button
                    type="button"
                    className={`nav-pill-btn ${!editorState.webViewMode && editorState.panelMode === "ai" ? "active" : ""}`}
                    onClick={handleAIClick}
                  >
                    <span className="nav-pill-icon" aria-hidden>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.01343 8.56433L4.53906 10.2246L4.0647 8.56433C3.81554 7.69229 3.13388 7.01063 2.26184 6.76147L0.601562 6.28711L2.26184 5.81274C3.13389 5.56359 3.81554 4.88193 4.0647 4.00989L4.53906 2.34961L5.01343 4.00989C5.26258 4.88193 5.94424 5.56359 6.81629 5.81274L8.47656 6.28711L6.81628 6.76147C5.94424 7.01063 5.26258 7.69229 5.01343 8.56433Z"
                          stroke="#1E1E1E"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.94007 4.37059L9.78906 4.97461L9.63806 4.37059C9.46171 3.66522 8.91096 3.11446 8.20558 2.93811L7.60156 2.78711L8.20558 2.6361C8.91096 2.45976 9.46171 1.909 9.63806 1.20363L9.78906 0.599609L9.94007 1.20363C10.1164 1.909 10.6672 2.45976 11.3725 2.6361L11.9766 2.78711L11.3725 2.93811C10.6672 3.11446 10.1164 3.66522 9.94007 4.37059Z"
                          stroke="#1E1E1E"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.14404 11.2847L8.91406 11.9746L8.68409 11.2847C8.55345 10.8928 8.24591 10.5852 7.85399 10.4546L7.16406 10.2246L7.85399 9.99463C8.24591 9.86399 8.55345 9.55645 8.68409 9.16454L8.91406 8.47461L9.14404 9.16454C9.27468 9.55645 9.58222 9.86399 9.97414 9.99463L10.6641 10.2246L9.97414 10.4546C9.58222 10.5852 9.27468 10.8928 9.14404 11.2847Z"
                          stroke="#1E1E1E"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Edit with AI
                  </button>
                )}
              </div>
            ) : (
              <b>EDITOR</b>
            )
          }
        ></Navbar>

        {fileManager.loading ? (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="mode-container">
            {!isPasswordPage && (
            <ResizablePane
              isOpen={!editorState.webViewMode}
              minWidth={420}
              maxWidth={80}
              initialWidth={40}
            >
              <CodePanel
                webViewMode={editorState.webViewMode}
                isSmallScreen={isSmallScreen}
                isMobileViewport={isMobileViewport}
                sidePanelView={editorState.sidePanelView}
                setSidePanelView={editorState.setSidePanelView}
                isFileExplorerVisible={editorState.isFileExplorerVisible}
                setIsFileExplorerVisible={editorState.setIsFileExplorerVisible}
                showCodeEditor={editorState.showCodeEditor}
                panelMode={editorState.panelMode}
                onHidePanel={() => editorState.setWebViewMode(true)}
                aiEnabled={aiEnabled}
                fileList={fileManager.fileList}
                setFileList={fileManager.setFileList}
                currentFile={currentFile}
                code={fileManager.code}
                codeLanguage={fileManager.codeLanguage}
                updateCode={fileManager.updateCode}
                saveFileContent={saveFileContent}
                switchFile={switchFile}
                handleCreateFile={callbacks.handleCreateFile}
                handleAIFilesChanged={handleAIFilesChanged}
                setIsPickerActive={editorState.setIsPickerActive}
                setHasChanged={editorState.setHasChanged}
                domain={domain}
                dbRef={fileManager.dbRef}
                iframeRef={iframeRef}
                editorRef={editorRef}
                onAIPaywallRequired={handleAIPaywallRequired}
              />
            </ResizablePane>
            )}

            <PreviewPanel
              isSmallScreen={isSmallScreen}
              iframeSrc={iframeSrc}
              iframeRef={iframeRef}
              onIframeLoad={handleIframeLoad}
              showPanel={editorState.showPanel}
              panelPosition={editorState.panelPosition}
              isLink={editorState.isLink}
              webViewMode={editorState.webViewMode}
              applyCommand={callbacks.applyCommand}
              handleLinkClick={callbacks.handleLinkClick}
              handleIframeLinkClick={callbacks.handleIframeLinkClick}
              removeLink={callbacks.removeLink}
              showCropDialog={imageCropping.showCropDialog}
              setShowCropDialog={imageCropping.setShowCropDialog}
              selectedImage={imageCropping.selectedImage}
              aspect={imageCropping.aspect}
              actualImage={imageCropping.actualImage}
              handleChanges={callbacks.handleChanges}
              accessToken={accessToken}
              domain={domain}
              showUpgradeModal={publishing.showUpgradeModal || aiPaywallVisible}
              closeUpgradeModal={closeAnyUpgradeModal}
              userProfile={
                aiPaywallVisible ? aiPaywallProfile : publishing.userProfile
              }
              modalContext={aiPaywallVisible ? "ai-chat" : "publish"}
            />
          </div>
        )}
      </div>
    </EditorProvider>
  );
}

export default App;
